import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  List,
  ListItem,
  Text,
  Flex,
  Tag,
  TagLabel,
  TagCloseButton,
  Icon,
  Spinner,
  useColorModeValue,
  useOutsideClick,
} from '@chakra-ui/react';
import { FiSearch, FiUser } from 'react-icons/fi';
import { supabase } from '../../../../utils/supabase';
import { useVisibilityScope } from '../../../../hooks/useVisibilityScope';
import { applyVisibilityScope, visibilityScopeKey } from '../../../../utils/recordVisibility';

/**
 * Searchable contact picker for selecting a test contact.
 * Queries crm_contacts as the user types (debounced), scoped to the viewer's
 * effective book (admins search everyone — previously this was unscoped for
 * all users, leaking contact names/emails/phones across books).
 */
export default function ContactTestPicker({ value, onChange, size = 'sm' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Admin viewers get { mode: 'all' } (search everyone); everyone else gets
  // their effective book (targetUserId null + non-admin → viewer's own uid).
  const { scope: effectiveScope } = useVisibilityScope({
    entityType: 'contact',
    targetUserId: null,
  });
  const scopeKey = visibilityScopeKey(effectiveScope) + ':g' + (effectiveScope?.grantsVersion || 0);

  const bg = useColorModeValue('white', 'gray.700');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useOutsideClick({ ref: containerRef, handler: () => setIsOpen(false) });

  useEffect(() => {
    if (!search || search.length < 2) {
      setResults([]);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const q = `%${search}%`;
        let query = supabase
          .from('crm_contacts')
          .select('id, first_name, last_name, email, phone')
          .eq('is_active', true)
          .or(`first_name.ilike.${q},last_name.ilike.${q},email.ilike.${q}`)
          .order('updated_at', { ascending: false })
          .limit(8);
        query = applyVisibilityScope(query, effectiveScope);
        const { data } = await query;
        setResults(data || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(debounceRef.current);
    // scopeKey covers effectiveScope content (incl. grant versions)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, scopeKey]);

  const handleSelect = (contact) => {
    onChange({
      id: contact.id,
      firstName: contact.first_name,
      lastName: contact.last_name,
      email: contact.email,
      phone: contact.phone,
    });
    setSearch('');
    setIsOpen(false);
  };

  if (value) {
    const name = `${value.firstName || ''} ${value.lastName || ''}`.trim() || value.email || 'Contact';
    return (
      <Tag size="md" colorScheme="blue" borderRadius="full" px={3} py={1}>
        <Icon as={FiUser} mr={1} boxSize={3} />
        <TagLabel fontSize="xs">Testing as: {name}</TagLabel>
        <TagCloseButton onClick={() => onChange(null)} />
      </Tag>
    );
  }

  return (
    <Box position="relative" ref={containerRef} minW="200px">
      <InputGroup size={size}>
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" boxSize={3.5} />
        </InputLeftElement>
        <Input
          placeholder="Search test contact..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
          onFocus={() => { if (search.length >= 2) setIsOpen(true); }}
        />
        {loading && (
          <InputRightElement>
            <Spinner size="xs" />
          </InputRightElement>
        )}
      </InputGroup>

      {isOpen && results.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          shadow="lg"
          zIndex={1500}
          maxH="250px"
          overflowY="auto"
        >
          <List spacing={0}>
            {results.map((c) => (
              <ListItem
                key={c.id}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => handleSelect(c)}
              >
                <Text fontSize="sm" fontWeight="medium">
                  {c.first_name} {c.last_name}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {c.email}{c.phone ? ` | ${c.phone}` : ''}
                </Text>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {isOpen && search.length >= 2 && !loading && results.length === 0 && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          bg={bg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          shadow="lg"
          zIndex={1500}
          p={3}
        >
          <Text fontSize="sm" color="gray.400">No contacts found</Text>
        </Box>
      )}
    </Box>
  );
}
